import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DocumentoAdjuntoPostulacion, TipoDocumentoPostulacion } from '../../../../../core/models/postulaciones.models';
import { FormularioEditable } from '../postulacion-formulario.models';

interface DocumentoRequeridoConfig {
  tipo: TipoDocumentoPostulacion;
  titulo: string;
  descripcion: string;
  obligatorio: boolean;
  extensionesPermitidas: string[];
  tamanoMaximoMb: number;
  tamanoMaximoZipMb?: number;
  sugerenciaPaginas?: string;
}

@Component({
  selector: 'app-postulacion-paso-documentos',
  imports: [CommonModule],
  templateUrl: './postulacion-paso-documentos.html',
  styleUrl: './postulacion-formulario-paso.shared.css',
})
export class PostulacionPasoDocumentos {
  readonly documentosRequeridos: DocumentoRequeridoConfig[] = [
    {
      tipo: 'carta_compromiso',
      titulo: 'Carta de compromiso institucional',
      descripcion: 'Documento de respaldo institucional para la postulacion.',
      obligatorio: true,
      extensionesPermitidas: ['pdf', 'docx'],
      tamanoMaximoMb: 10,
      sugerenciaPaginas: 'Maximo sugerido: 2 paginas.',
    },
    {
      tipo: 'cv_postulante',
      titulo: 'Curriculum vitae del postulante',
      descripcion: 'Antecedentes curriculares del postulante principal.',
      obligatorio: true,
      extensionesPermitidas: ['pdf', 'docx'],
      tamanoMaximoMb: 5,
      sugerenciaPaginas: 'Maximo sugerido: 5 paginas.',
    },
    {
      tipo: 'presupuesto_financiero',
      titulo: 'Presupuesto o planilla financiera',
      descripcion: 'Resumen financiero o planilla principal de recursos.',
      obligatorio: true,
      extensionesPermitidas: ['pdf', 'xlsx'],
      tamanoMaximoMb: 10,
      sugerenciaPaginas: 'Sin limite sugerido de paginas para planillas.',
    },
    {
      tipo: 'anexo_tecnico',
      titulo: 'Anexo tecnico complementario',
      descripcion: 'Soporte tecnico o conjunto de anexos adicionales del concurso.',
      obligatorio: false,
      extensionesPermitidas: ['pdf', 'docx', 'xlsx', 'zip'],
      tamanoMaximoMb: 30,
      tamanoMaximoZipMb: 50,
      sugerenciaPaginas: 'Maximo sugerido: 50 paginas o ZIP tecnico.',
    },
    {
      tipo: 'evidencia_visual',
      titulo: 'Imagenes o evidencia visual',
      descripcion: 'Graficos, fotos o evidencia visual de apoyo.',
      obligatorio: false,
      extensionesPermitidas: ['jpg', 'jpeg', 'png'],
      tamanoMaximoMb: 5,
    },
  ];

  @Input({ required: true }) formulario!: FormularioEditable;
  @Input() soloLectura = false;
  @Output() changed = new EventEmitter<void>();
  mensajeDocumento = '';

  obtenerDocumento(tipo: TipoDocumentoPostulacion): DocumentoAdjuntoPostulacion | undefined {
    return this.formulario.documentosAdjuntos.find((documento) => documento.tipo === tipo);
  }

  acceptPara(config: DocumentoRequeridoConfig): string {
    return config.extensionesPermitidas.map((extension) => `.${extension}`).join(',');
  }

  seleccionarDocumento(event: Event, config: DocumentoRequeridoConfig): void {
    if (this.soloLectura) return;

    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];

    if (!file) return;

    const extension = this.obtenerExtension(file.name);
    if (!config.extensionesPermitidas.includes(extension)) {
      this.mensajeDocumento = `El archivo ${file.name} no cumple con los formatos permitidos para ${config.titulo}.`;
      input.value = '';
      return;
    }

    const tamanoMaximoMb = extension === 'zip' && config.tamanoMaximoZipMb ? config.tamanoMaximoZipMb : config.tamanoMaximoMb;
    const tamanoMaximoBytes = tamanoMaximoMb * 1024 * 1024;
    if (file.size > tamanoMaximoBytes) {
      this.mensajeDocumento = `El archivo ${file.name} supera el maximo permitido de ${tamanoMaximoMb} MB para ${config.titulo}.`;
      input.value = '';
      return;
    }

    const adjunto: DocumentoAdjuntoPostulacion = {
      id: `${config.tipo}-${Date.now()}`,
      tipo: config.tipo,
      nombreArchivo: file.name,
      extension,
      tamanoBytes: file.size,
      obligatorio: config.obligatorio,
      estado: 'cargado',
      fechaCarga: new Date().toISOString(),
      origen: 'manual',
    };

    this.formulario.documentosAdjuntos = [
      ...this.formulario.documentosAdjuntos.filter((documento) => documento.tipo !== config.tipo),
      adjunto,
    ];
    this.mensajeDocumento = `Documento ${file.name} cargado correctamente en ${config.titulo}.`;
    this.changed.emit();
    input.value = '';
  }

  eliminarDocumento(tipo: TipoDocumentoPostulacion): void {
    if (this.soloLectura) return;

    const documento = this.obtenerDocumento(tipo);
    this.formulario.documentosAdjuntos = this.formulario.documentosAdjuntos.filter((item) => item.tipo !== tipo);
    this.mensajeDocumento = documento
      ? `Documento ${documento.nombreArchivo} eliminado del borrador.`
      : 'Documento eliminado del borrador.';
    this.changed.emit();
  }

  formatearTamano(bytes: number): string {
    if (bytes < 1024 * 1024) {
      return `${Math.round(bytes / 1024)} KB`;
    }

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private obtenerExtension(nombreArchivo: string): string {
    return nombreArchivo.split('.').pop()?.toLowerCase() ?? '';
  }
}
